import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";
import { useState, useEffect, useMemo, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { Loader2, Heart, MapPin, Building2, Search, ArrowUpDown, AlertCircle, ChevronLeft, ChevronRight } from "lucide-react";
import { useHeaderHeight } from "@/hooks/use-header-height";
import { FornecedorCardSkeleton } from "@/components/ui/skeletons";
import { useAuth } from "@/hooks/use-auth";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import debounce from "lodash.debounce";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

type FavoritosSearch = {
  q?: string;
  order?: string;
  page?: number;
}

const ITEMS_PER_PAGE = 24;

export const Route = createFileRoute("/_authenticated/favoritos")({
  validateSearch: (search: Record<string, unknown>): FavoritosSearch => {
    return {
      q: (search['q'] as string) || "",
      order: (search['order'] as string) || "default",
      page: Number(search['page']) || 0,
    };
  },
  component: FavoritosList,
});

function FavoritosList() {
  const { q, order, page } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const headerHeight = useHeaderHeight();
  const [searchInput, setSearchInput] = useState(q || "");

  const { data: favoritos = [], isLoading: loading, error: queryError } = useQuery({
    queryKey: ['favoritos', user?.id],
    queryFn: async () => {
      if (!user) return [];
      const { data, error } = await supabase
        .from('favoritos')
        .select('id, fornecedores(id, nome, cidade, uf, logo_url, verificado, destaque, segmentos(nome, icone))')
        .eq('user_id', user.id);
      if (error) throw error;
      return data || [];
    },
    enabled: !!user,
    staleTime: 1000 * 60 * 5,
  });

  const error = queryError ? "Não foi possível carregar seus favoritos." : null;

  // Cache para favoritos
  const cache = useRef<{ data: any[], timestamp: number } | null>(null);
  const CACHE_TTL = 1000 * 60 * 5; // 5 minutos


  const debouncedSearch = useMemo(
    () => debounce((val: string) => {
      navigate({ search: (prev: FavoritosSearch) => ({ ...prev, q: val, page: 0 }) });
    }, 400),
    [navigate]
  );

  useEffect(() => {
    setSearchInput(q || "");
  }, [q]);

  useEffect(() => {
    // React Query handles loading now
  }, []);


  const filteredFavoritos = useMemo(() => {
    let result = [...favoritos];
    
    if (q) {
      const searchTerm = q.toLowerCase();
      result = result.filter(fav => 
        (fav.fornecedores.nome?.toLowerCase().includes(searchTerm)) ||
        (fav.fornecedores.cidade?.toLowerCase().includes(searchTerm))
      );
    }

    if (order === "name") {
      result.sort((a, b) => a.fornecedores.nome.localeCompare(b.fornecedores.nome));
    } else {
      // default: addition date
      result.sort((a, b) => b.id.localeCompare(a.id));
    }

    return result;
  }, [favoritos, q, order]);

  const totalCount = filteredFavoritos.length;
  const paginatedFavoritos = useMemo(() => {
    const from = (page || 0) * ITEMS_PER_PAGE;
    const to = from + ITEMS_PER_PAGE;
    return filteredFavoritos.slice(from, to);
  }, [filteredFavoritos, page]);

  const handleRemoveFavorite = async (e: React.MouseEvent, id: string) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const { error: removeError } = await supabase
        .from('favoritos')
        .delete()
        .eq('id', id);
      
      if (removeError) throw removeError;
      queryClient.invalidateQueries({ queryKey: ['favoritos'] });
    } catch (err) {
      console.error("Error removing favorite:", err);
    }
  };

  return (
    <div className="bg-secondary/30 min-h-screen">
      <div 
        className="bg-[#C6F764] py-4 sticky z-40 shadow-[0_1px_3px_rgba(12,26,18,0.08)]"
        style={{ top: headerHeight }}
      >
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="relative w-full md:max-w-xl group">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6B7280] group-focus-within:text-[#0C1A12] transition-colors" />
              <Input 
                placeholder="Buscar nos favoritos..." 
                className="pl-12 h-12 rounded-[10px] bg-white border-none shadow-sm focus-visible:ring-2 focus-visible:ring-[#0C1A12] focus-visible:ring-offset-0 placeholder:text-[#6B7280] text-[#0C1A12]"
                value={searchInput}
                onChange={(e) => {
                  setSearchInput(e.target.value);
                  debouncedSearch(e.target.value);
                }}
              />
            </div>

            <div className="flex items-center gap-3 w-full md:w-auto">
              <Select value={order || "default"} onValueChange={(val) => navigate({ search: (prev: FavoritosSearch) => ({ ...prev, order: val, page: 0 }) })}>
                <SelectTrigger className="w-full md:w-48 rounded-[10px] h-12 bg-white border-none shadow-sm text-[#0C1A12] focus:ring-2 focus:ring-[#0C1A12] focus:ring-offset-0">
                  <div className="flex items-center gap-2"><ArrowUpDown className="w-4 h-4" /><SelectValue /></div>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">Data de adição</SelectItem>
                  <SelectItem value="name">Nome (A-Z)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>

      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="container mx-auto max-w-6xl py-8 px-4">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold">Meus Favoritos</h1>
          {!loading && totalCount > 0 && (
            <span className="text-sm font-medium text-muted-foreground">{totalCount} {totalCount === 1 ? 'fornecedor' : 'fornecedores'}</span>
          )}
        </div>

        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <FornecedorCardSkeleton key={i} />
            ))}
          </div>
        ) : error ? (
          <div className="bg-white rounded-3xl p-16 text-center border shadow-sm">
            <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-6 text-red-500">
              <AlertCircle className="w-10 h-10" />
            </div>
            <h2 className="text-2xl font-bold mb-3 text-brand-dark">Ops! Algo deu errado</h2>
            <p className="text-muted-foreground mb-8 max-w-sm mx-auto">{error}</p>
            <Button onClick={() => window.location.reload()} variant="outline" className="rounded-xl px-8 border-brand-dark hover:bg-secondary">Recarregar página</Button>
          </div>
        ) : favoritos.length === 0 ? (
          <div className="bg-white rounded-3xl p-12 text-center border shadow-sm">
            <div className="w-20 h-20 bg-secondary rounded-full flex items-center justify-center mx-auto mb-6">
              <Heart className="w-10 h-10 text-muted-foreground" />
            </div>
            <h2 className="text-2xl font-bold mb-3">Sua lista está vazia</h2>
            <p className="text-muted-foreground mb-8 max-w-sm mx-auto">
              Salve os fornecedores que você mais gostou clicando no coração para facilitar seu contato no futuro.
            </p>
            <Button asChild variant="onLight" className="rounded-xl font-bold">
              <Link to="/fornecedores" search={{}}>Explorar fornecedores</Link>
            </Button>
          </div>
        ) : paginatedFavoritos.length === 0 ? (
          <div className="bg-white rounded-3xl p-12 text-center border shadow-sm">
            <div className="w-20 h-20 bg-secondary rounded-full flex items-center justify-center mx-auto mb-6">
              <Search className="w-10 h-10 text-muted-foreground" />
            </div>
            <h2 className="text-2xl font-bold mb-3">Nenhum favorito encontrado</h2>
            <p className="text-muted-foreground mb-8 max-w-sm mx-auto">
              Não encontramos nenhum favorito com o termo "{q}".
            </p>
            <Button onClick={() => navigate({ search: (prev: FavoritosSearch) => ({ ...prev, q: "", page: 0 }) })} variant="outlineLight" className="rounded-xl px-8 border-brand-dark">Limpar busca</Button>
          </div>
        ) : (
          <>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
              {paginatedFavoritos.map((fav) => {
                const f = fav.fornecedores;
                return (
                  <Card 
                    key={fav.id} 
                    className="bg-white p-6 border border-[#E5E7EB] rounded-[16px] shadow-sm hover:shadow-xl transition-all group relative cursor-pointer"
                    onClick={() => navigate({ to: `/fornecedores/${f.id}` as any })}
                  >

                    <div className="flex justify-between items-start mb-4">
                      <div className="w-14 h-14 bg-secondary/50 rounded-2xl flex items-center justify-center overflow-hidden border border-gray-100">
                        {f.logo_url ? (
                          <img src={f.logo_url} alt={f.nome} className="w-full h-full object-cover" />
                        ) : (
                          <span className="font-bold text-xl text-gray-400">{f.nome[0]}</span>
                        )}
                      </div>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="text-red-500 hover:bg-red-50 rounded-full"
                        onClick={(e) => handleRemoveFavorite(e, fav.id)}
                      >
                        <Heart className="w-6 h-6 fill-current" />
                      </Button>
                    </div>

                    <h3 className="text-xl font-bold mb-2 group-hover:text-[#0C1A12] transition-colors leading-tight line-clamp-1">{f.nome}</h3>

                    
                    <div className="flex flex-wrap gap-2 mb-4">
                      <Badge variant="outline" className="bg-secondary/30 border-none text-[10px] font-bold uppercase tracking-wider py-1 px-2.5 rounded-lg flex items-center gap-1.5">
                        <Building2 className="w-3 h-3" />
                        {f.segmentos?.nome}
                      </Badge>
                      <Badge variant="outline" className="bg-secondary/30 border-none text-[10px] font-bold uppercase tracking-wider py-1 px-2.5 rounded-lg flex items-center gap-1.5">
                        <MapPin className="w-3 h-3" />
                        {f.cidade}/{f.uf}
                      </Badge>
                    </div>

                    <Button 
                      variant="onLight" 
                      className="w-full rounded-xl font-bold h-12 bg-[#0C1A12] text-white"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate({ to: `/fornecedores/${f.id}` as any });
                      }}
                    >
                      Ver contato
                    </Button>

                  </Card>
                );
              })}
            </div>

            {/* Pagination */}
            {totalCount > ITEMS_PER_PAGE && (
              <div className="flex items-center justify-center gap-2 pb-12">
                <Button 
                  variant="outlineLight" 
                  size="icon" 
                  className="rounded-xl w-12 h-12" 
                  disabled={page === 0}
                  onClick={() => {
                    navigate({ search: (prev: FavoritosSearch) => ({ ...prev, page: page! - 1 }) });
                    window.scrollTo(0, 0);
                  }}
                >
                  <ChevronLeft className="w-5 h-5" />
                </Button>
                <span className="text-sm font-bold mx-4 bg-white px-4 py-2 rounded-xl border">
                  Página {page! + 1} de {Math.ceil(totalCount / ITEMS_PER_PAGE)}
                </span>
                <Button 
                  variant="outlineLight" 
                  size="icon" 
                  className="rounded-xl w-12 h-12" 
                  disabled={(page! + 1) * ITEMS_PER_PAGE >= totalCount}
                  onClick={() => {
                    navigate({ search: (prev: FavoritosSearch) => ({ ...prev, page: page! + 1 }) });
                    window.scrollTo(0, 0);
                  }}
                >
                  <ChevronRight className="w-5 h-5" />
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
